import { Box, Button } from "@mui/material";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";

const manifestRoot = process.env.REACT_APP_WEB_ROOT || 'https://colbrensportsprod.blob.core.windows.net/web';

type Variant = { fileName: string; width: number; height: number };

type Page = {
  id: string;           // e.g., "p1"
  label: string;        // e.g., "Page 1"
  variants: Variant[];  // largest first is ideal
};

type Manifest = {
  title?: string;        // optional, fallback to id
  pages: Page[];
  generatedAt?: string;
};


function SkeletonPage() {
  return (
    <div className="relative mb-4 h-[60vh] w-full animate-pulse rounded-xl border border-zinc-800 bg-zinc-800/40" />
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="mx-auto max-w-xl rounded-xl border border-red-400 bg-red-950/20 p-4 text-red-200">
      <div className="font-semibold">Cannot load program</div>
      <div className="text-sm opacity-90">{message}</div>
    </div>
  );
}

async function fetchManifest(url: string): Promise<Manifest> {
  const res = await fetch(url, { cache: "no-store" });
  if (!res.ok) throw new Error(`Failed to load manifest: ${res.status}`);
  return res.json();
}

export  function OnlineProgramPage() {
  
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

   const params = useParams();
   useDocumentTitle("Online Gameday Program");

   const onlineRoot = `${manifestRoot}/programs/${params.programGuid}`;
   const pdfHref = `${onlineRoot}/gameday-program.pdf`;
   const manifestUrl = `${onlineRoot}/program-manifest.json`;

   useEffect(() => {
       initPage();
   }, []);

   async function initPage(){

    console.log(`Loading online program for GUID: ${params.programGuid}`);

       if (!params.programGuid) {
           throw Error('Program Guid is missing');
       }
       
       const thisManifest = await fetchManifest(manifestUrl);
       setManifest(thisManifest);
    }

  return (
    <Box style={{ backgroundColor: '#232425', minHeight: '100vh' }}>
     <div id="parentDiv" className="min-h-screen bg-[#232425] text-white flex flex-col items-center">
     <header className="sticky top-0 z-10 flex items-center gap-3 border-b border-zinc-800 bg-zinc-950/90 px-4 py-3 backdrop-blur">
        <div className="leading-tight">
          <h1 className="text-sm font-semibold" style={{ color: '#ffffff' }}>
            Gameday Program
          </h1>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="contained"
            size="small"
          
            href={pdfHref || undefined}
            
            sx={{
              textTransform: 'none',
              borderRadius: '999px',
              backgroundColor: '#111827',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.06)',
              '&:hover': { backgroundColor: '#0b1220' }
            }}
          >
            Download PDF
          </Button> 
        </div>
      </header>

  
      <main className="mx-auto max-w-5xl px-3 py-4 flex flex-col items-center">
        {!params.programGuid && <ErrorState message="Missing ?id=... in the URL" />}
        {loading && (
          <div className="space-y-4 w-full">
            <SkeletonPage />
          </div>
        )}
        {!loading && error && <ErrorState message={error} />}

        {!loading && !error && manifest && (
          <div className="relative w-full">
            {manifest.pages.map((p, idx) => {
              const largest = p.variants[0];
              const srcset = p.variants.map(v => `${onlineRoot}${v.fileName} ${v.width}w`).join(", ");
              const sizes = "(max-width: 900px) 100vw, 900px";
              return (
                <section key={p.id} id={p.id} className="relative mb-6 w-full flex justify-center">
                  
                  <img
                    loading={idx === 0 ? "eager" : "lazy"}
                    alt={p.label}
                    src={`${onlineRoot}${largest.fileName}`}
                    srcSet={srcset}
                    sizes={sizes}
                    width={largest.width}
                    height={largest.height}
                    className="block rounded-xl border border-zinc-800 bg-black"
                    style={{   display: 'block', width: '100%', height: 'auto', maxWidth: `${largest.width}px`,margin: '0 auto' }}
                  />
                  <div className="absolute bottom-2 right-2 rounded-md bg-black/60 px-2 py-1 text-xs">{idx + 1}</div>
                </section>
              );
            })}

          </div>
        )}
      </main>

    </div>
    </Box>
    
  );
}

